// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import type {
  RecipesRecipe,
  RecipesIngredient,
  RecipesFoodItem,
  RecipesRecipeNutrition,
  RecipesTag,
  RecipesRecipeTag,
  RecipesRecipeFavorite,
  RecipesRecipeCollection,
  RecipesCollectionRecipe,
  RecipesEnrichmentJob,
  RecipesMealSchedule,
  RecipesScheduledMeal,
  RecipesNutritionSummary,
  RecipesShoppingList,
  RecipesShoppingListItem,
} from "../_internal/types.gen";

import {
  // Recipe
  getAdminRecipesById,
  getAdminRecipesWorkspaceByWorkspaceId,
  getAdminRecipesWorkspaceByWorkspaceIdContactByContactId,
  getAdminRecipesWorkspaceByWorkspaceIdPublished,
  postAdminRecipes,
  postAdminRecipesImport,
  patchAdminRecipesById,
  deleteAdminRecipesById,
  patchAdminRecipesByIdReEnrich,
  patchAdminRecipesByIdArchive,
  patchAdminRecipesByIdRestore,
  // Ingredient
  getAdminRecipesIngredientsById,
  getAdminRecipesIngredientsRecipeByRecipeId,
  postAdminRecipesIngredients,
  patchAdminRecipesIngredientsById,
  deleteAdminRecipesIngredientsById,
  // FoodItem
  getAdminRecipesFoodItemsById,
  getAdminRecipesFoodItemsApplicationByApplicationId,
  postAdminRecipesFoodItems,
  patchAdminRecipesFoodItemsById,
  deleteAdminRecipesFoodItemsById,
  // RecipeNutrition
  getAdminRecipesRecipeNutritionsById,
  getAdminRecipesRecipeNutritionsRecipeByRecipeId,
  postAdminRecipesRecipeNutritions,
  patchAdminRecipesRecipeNutritionsById,
  // Tag
  getAdminRecipesTagsById,
  getAdminRecipesTagsApplicationByApplicationId,
  postAdminRecipesTags,
  patchAdminRecipesTagsById,
  deleteAdminRecipesTagsById,
  // RecipeFavorite
  getAdminRecipesFavoritesById,
  getAdminRecipesFavoritesWorkspaceByWorkspaceId,
  getAdminRecipesFavoritesRecipeByRecipeId,
  postAdminRecipesFavorites,
  patchAdminRecipesFavoritesById,
  deleteAdminRecipesFavoritesById,
  // RecipeCollection
  getAdminRecipesCollectionsById,
  getAdminRecipesCollectionsWorkspaceByWorkspaceId,
  postAdminRecipesCollections,
  patchAdminRecipesCollectionsById,
  deleteAdminRecipesCollectionsById,
  // CollectionRecipe
  getAdminRecipesCollectionRecipesById,
  getAdminRecipesCollectionRecipesCollectionByCollectionId,
  postAdminRecipesCollectionRecipes,
  patchAdminRecipesCollectionRecipesById,
  deleteAdminRecipesCollectionRecipesById,
  // RecipeTag
  getAdminRecipesRecipeTagsById,
  getAdminRecipesRecipeTagsRecipeByRecipeId,
  postAdminRecipesRecipeTags,
  deleteAdminRecipesRecipeTagsById,
  // EnrichmentJob
  getAdminRecipesEnrichmentJobsById,
  getAdminRecipesEnrichmentJobsRecipeByRecipeId,
  // MealSchedule
  getAdminRecipesMealSchedulesById,
  getAdminRecipesMealSchedulesWorkspaceByWorkspaceId,
  postAdminRecipesMealSchedules,
  patchAdminRecipesMealSchedulesById,
  deleteAdminRecipesMealSchedulesById,
  // ScheduledMeal
  getAdminRecipesScheduledMealsById,
  getAdminRecipesScheduledMealsMealScheduleByMealScheduleId,
  postAdminRecipesScheduledMeals,
  patchAdminRecipesScheduledMealsById,
  deleteAdminRecipesScheduledMealsById,
  // NutritionSummary
  getAdminRecipesNutritionSummariesById,
  getAdminRecipesNutritionSummariesMealScheduleByMealScheduleId,
  // ShoppingList
  getAdminRecipesShoppingListsById,
  getAdminRecipesShoppingListsWorkspaceByWorkspaceId,
  postAdminRecipesShoppingLists,
  patchAdminRecipesShoppingListsById,
  deleteAdminRecipesShoppingListsById,
  // ShoppingListItem
  getAdminRecipesShoppingListItemsById,
  getAdminRecipesShoppingListItemsShoppingListByShoppingListId,
  postAdminRecipesShoppingListItems,
  patchAdminRecipesShoppingListItemsById,
  deleteAdminRecipesShoppingListItemsById,
  // New actions
  postAdminRecipesBulkImport,
  postAdminRecipesShoppingListsGenerate,
  postAdminRecipesShoppingListsRegenerate,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";
import { buildPageQuery } from "../namespace-types";

// ── Attribute interfaces ────────────────────────────────────────────

/** Attributes accepted when creating a recipe. */
export interface CreateRecipeAttributes {
  workspace_id: string;
  contact_id?: string;
  application_id?: string;
  title: string;
  description?: string;
  instructions?: string;
  source_type?: "owned" | "external" | "imported";
  source_uri?: string;
  source_provider?: string;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  servings?: number;
  difficulty?: "easy" | "medium" | "hard";
  cuisine_type?: string;
  image_url?: string;
  image_urls?: string[];
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when importing an external recipe. */
export interface ImportRecipeAttributes {
  workspace_id: string;
  contact_id?: string;
  application_id?: string;
  title: string;
  description?: string;
  instructions?: string;
  source_uri?: string;
  source_url?: string;
  source_name?: string;
  source_provider?: string;
  /** Write-only — raw connector response stored server-side but never returned in API responses. */
  source_data?: Record<string, unknown>;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  servings?: number;
  difficulty?: "easy" | "medium" | "hard";
  cuisine_type?: string;
  image_url?: string;
  image_urls?: string[];
  metadata?: Record<string, unknown>;
}

/** Error entry in a bulk import failure. */
export interface BulkImportError {
  index?: number;
  errors?: Array<{
    field?: string;
    message?: string;
    code?: string;
  }>;
}

/** Result of a bulk import operation. */
export interface BulkImportResult {
  imported: RecipesRecipe[];
  failed: BulkImportError[];
}

/** Attributes accepted when updating a recipe. */
export interface UpdateRecipeAttributes {
  title?: string;
  description?: string;
  instructions?: string;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  servings?: number;
  difficulty?: "easy" | "medium" | "hard";
  cuisine_type?: string;
  image_url?: string;
  image_urls?: string[];
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when creating an ingredient. */
export interface CreateIngredientAttributes {
  recipe_id: string;
  food_item_id?: string;
  name: string;
  quantity?: number;
  unit?: string;
  preparation_note?: string;
  sort_order?: number;
  food_group?: string;
}

/** Attributes accepted when updating an ingredient. */
export interface UpdateIngredientAttributes {
  food_item_id?: string;
  name?: string;
  quantity?: number;
  unit?: string;
  preparation_note?: string;
  sort_order?: number;
  food_group?: string;
}

/** Attributes accepted when creating a food item. */
export interface CreateFoodItemAttributes {
  application_id: string;
  name: string;
  external_id?: string;
  external_source?: string;
  nutrition_per_100g?: Record<string, unknown>;
  food_group?: string;
  allergens?: string[];
  default_unit?: string;
  unit_conversions?: Record<string, unknown>;
}

/** Attributes accepted when updating a food item. */
export interface UpdateFoodItemAttributes {
  name?: string;
  nutrition_per_100g?: Record<string, unknown>;
  food_group?: string;
  allergens?: string[];
  default_unit?: string;
  unit_conversions?: Record<string, unknown>;
}

/** Attributes accepted when creating recipe nutrition. */
export interface CreateRecipeNutritionAttributes {
  recipe_id: string;
  source?: "computed" | "external" | "manual";
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  fiber_g?: number;
  sugar_g?: number;
  sodium_mg?: number;
  full_nutrients?: Record<string, unknown>;
}

/** Attributes accepted when updating recipe nutrition. */
export interface UpdateRecipeNutritionAttributes {
  source?: "computed" | "external" | "manual";
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  fiber_g?: number;
  sugar_g?: number;
  sodium_mg?: number;
  full_nutrients?: Record<string, unknown>;
}

/** Attributes accepted when creating a tag. */
export interface CreateTagAttributes {
  application_id: string;
  category: "cuisine" | "diet" | "health" | "course" | "technique" | "custom";
  name: string;
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when updating a tag. */
export interface UpdateTagAttributes {
  name?: string;
  category?: "cuisine" | "diet" | "health" | "course" | "technique" | "custom";
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when creating a recipe-tag association. */
export interface CreateRecipeTagAttributes {
  recipe_id: string;
  tag_id: string;
}

/** Attributes accepted when creating a recipe favorite. */
export interface CreateRecipeFavoriteAttributes {
  workspace_id: string;
  contact_id?: string;
  recipe_id: string;
  notes?: string;
  meal_type?: string;
  /** Integer rating value. */
  rating?: number;
}

/** Attributes accepted when updating a recipe favorite. */
export interface UpdateRecipeFavoriteAttributes {
  notes?: string;
  meal_type?: string;
  /** Integer rating value. */
  rating?: number;
}

/** Attributes accepted when creating a recipe collection. */
export interface CreateRecipeCollectionAttributes {
  workspace_id: string;
  contact_id?: string;
  name: string;
  description?: string;
  scope?: "workspace" | "contact";
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when updating a recipe collection. */
export interface UpdateRecipeCollectionAttributes {
  name?: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

/** Attributes accepted when adding a recipe to a collection. */
export interface CreateCollectionRecipeAttributes {
  collection_id: string;
  recipe_id: string;
  sort_order?: number;
}

/** Attributes accepted when updating a collection recipe. */
export interface UpdateCollectionRecipeAttributes {
  sort_order?: number;
}

/** Attributes accepted when creating a meal schedule. */
export interface CreateMealScheduleAttributes {
  workspace_id: string;
  contact_id?: string;
  name: string;
  start_date?: string;
  end_date?: string;
  status?: "draft" | "active" | "completed";
}

/** Attributes accepted when updating a meal schedule. */
export interface UpdateMealScheduleAttributes {
  name?: string;
  start_date?: string;
  end_date?: string;
  status?: "draft" | "active" | "completed";
}

/** Attributes accepted when creating a scheduled meal. */
export interface CreateScheduledMealAttributes {
  meal_schedule_id: string;
  recipe_id: string;
  date: string;
  meal_type: "breakfast" | "lunch" | "dinner" | "snack";
  servings?: number;
}

/** Attributes accepted when updating a scheduled meal. */
export interface UpdateScheduledMealAttributes {
  date?: string;
  meal_type?: "breakfast" | "lunch" | "dinner" | "snack";
  servings?: number;
}

/** Attributes accepted when creating a shopping list. */
export interface CreateShoppingListAttributes {
  workspace_id: string;
  contact_id?: string;
  meal_schedule_id?: string;
  name: string;
  status?: "draft" | "active" | "completed";
}

/** Attributes accepted when updating a shopping list. */
export interface UpdateShoppingListAttributes {
  name?: string;
  status?: "draft" | "active" | "completed";
}

/** Attributes accepted when creating a shopping list item. */
export interface CreateShoppingListItemAttributes {
  shopping_list_id: string;
  name: string;
  total_quantity?: number;
  unit?: string;
  food_group?: string;
  checked?: boolean;
}

/** Attributes accepted when updating a shopping list item. */
export interface UpdateShoppingListItemAttributes {
  name?: string;
  total_quantity?: number;
  unit?: string;
  food_group?: string;
  checked?: boolean;
}

// ── Pagination ──────────────────────────────────────────────────────

export type ListOptions = { page?: number; pageSize?: number } & RequestOptions;

/** Options for listing recipe collections. */
export interface CollectionListOptions extends ListOptions {
  /** Filter to a specific contact's collections. */
  contactId?: string;
  /** Filter by collection scope. */
  scope?: "workspace" | "contact";
  /** Sort field(s). Prefix with '-' for descending (e.g. '-inserted_at'). */
  sort?: string;
}

// ── Namespace ───────────────────────────────────────────────────────

export type RecipesAPI = ReturnType<typeof createRecipesNamespace>;

export function createRecipesNamespace(rb: RequestBuilder) {
  return {
    // ── Recipe ────────────────────────────────────────────────────

    /** Get a recipe by ID. */
    async get(id: string, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        getAdminRecipesById,
        { path: { id } },
        options,
      );
    },

    /** List recipes in a workspace (optional status filter, created_by filter). */
    async list(
      workspaceId: string,
      options?: ListOptions & {
        filter?: { status?: string; created_by?: string };
      },
    ) {
      return rb.execute<RecipesRecipe[]>(
        getAdminRecipesWorkspaceByWorkspaceId,
        {
          path: { workspace_id: workspaceId },
          query: {
            ...buildPageQuery(options?.page, options?.pageSize)?.query,
            ...(options?.filter?.status
              ? { status: options.filter.status }
              : {}),
            ...(options?.filter?.created_by
              ? { created_by: options.filter.created_by }
              : {}),
          },
        },
        options,
      );
    },

    /** List recipes for a contact in a workspace. */
    async listByContact(
      workspaceId: string,
      contactId: string,
      options?: ListOptions,
    ) {
      return rb.execute<RecipesRecipe[]>(
        getAdminRecipesWorkspaceByWorkspaceIdContactByContactId,
        {
          path: { workspace_id: workspaceId, contact_id: contactId },
          query: { ...buildPageQuery(options?.page, options?.pageSize)?.query },
        },
        options,
      );
    },

    /** List published recipes for a workspace. */
    async listPublished(workspaceId: string, options?: ListOptions) {
      return rb.execute<RecipesRecipe[]>(
        getAdminRecipesWorkspaceByWorkspaceIdPublished,
        {
          path: { workspace_id: workspaceId },
          query: { ...buildPageQuery(options?.page, options?.pageSize)?.query },
        },
        options,
      );
    },

    /** Create a recipe. */
    async create(attrs: CreateRecipeAttributes, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        postAdminRecipes,
        { body: { data: { type: "recipes-recipe", attributes: attrs } } },
        options,
      );
    },

    /** Import an external recipe. */
    async import(attrs: ImportRecipeAttributes, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        postAdminRecipesImport,
        { body: { data: { type: "recipes-recipe", attributes: attrs } } },
        options,
      );
    },

    /**
     * Bulk import recipes with dedup via source_uri.
     *
     * @param recipes - Array of recipe attribute maps
     * @param options - Request options
     * @returns Object with `imported` and `failed` arrays
     */
    async bulkImport(
      recipes: ImportRecipeAttributes[],
      options?: RequestOptions,
    ): Promise<BulkImportResult> {
      return rb.execute<BulkImportResult>(
        postAdminRecipesBulkImport,
        { body: { data: { recipes } } },
        options,
      );
    },

    /** Update a recipe. */
    async update(
      id: string,
      attrs: UpdateRecipeAttributes,
      options?: RequestOptions,
    ) {
      return rb.execute<RecipesRecipe>(
        patchAdminRecipesById,
        {
          path: { id },
          body: { data: { type: "recipes-recipe", id, attributes: attrs } },
        },
        options,
      );
    },

    /**
     * Re-enqueue AI enrichment for a recipe.
     * Resets enrichment_status to pending and triggers the enrichment pipeline.
     *
     * @param id - Recipe ID
     * @param options - Request options
     * @returns The updated recipe
     */
    async reEnrich(id: string, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        patchAdminRecipesByIdReEnrich,
        { path: { id }, body: { data: { type: "recipes-recipe", id } } },
        options,
      );
    },

    /** Archive a recipe. */
    async archive(id: string, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        patchAdminRecipesByIdArchive,
        { path: { id }, body: { data: { type: "recipes-recipe", id } } },
        options,
      );
    },

    /** Restore an archived recipe. */
    async restore(id: string, options?: RequestOptions) {
      return rb.execute<RecipesRecipe>(
        patchAdminRecipesByIdRestore,
        { path: { id }, body: { data: { type: "recipes-recipe", id } } },
        options,
      );
    },

    /** Delete a recipe. */
    async delete(id: string, options?: RequestOptions) {
      return rb.execute<void>(
        deleteAdminRecipesById,
        { path: { id } },
        options,
      );
    },

    // ── Ingredient ────────────────────────────────────────────────

    ingredients: {
      /** Get an ingredient by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesIngredient>(
          getAdminRecipesIngredientsById,
          { path: { id } },
          options,
        );
      },

      /** List ingredients for a recipe. */
      async list(recipeId: string, options?: ListOptions) {
        return rb.execute<RecipesIngredient[]>(
          getAdminRecipesIngredientsRecipeByRecipeId,
          {
            path: { recipe_id: recipeId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create an ingredient. */
      async create(
        attrs: CreateIngredientAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesIngredient>(
          postAdminRecipesIngredients,
          { body: { data: { type: "recipes-ingredient", attributes: attrs } } },
          options,
        );
      },

      /** Update an ingredient. */
      async update(
        id: string,
        attrs: UpdateIngredientAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesIngredient>(
          patchAdminRecipesIngredientsById,
          {
            path: { id },
            body: {
              data: { type: "recipes-ingredient", id, attributes: attrs },
            },
          },
          options,
        );
      },

      /** Delete an ingredient. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesIngredientsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── FoodItem ──────────────────────────────────────────────────

    foodItems: {
      /** Get a food item by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesFoodItem>(
          getAdminRecipesFoodItemsById,
          { path: { id } },
          options,
        );
      },

      /** List food items for an application. */
      async list(applicationId: string, options?: ListOptions) {
        return rb.execute<RecipesFoodItem[]>(
          getAdminRecipesFoodItemsApplicationByApplicationId,
          {
            path: { application_id: applicationId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a food item. */
      async create(attrs: CreateFoodItemAttributes, options?: RequestOptions) {
        return rb.execute<RecipesFoodItem>(
          postAdminRecipesFoodItems,
          { body: { data: { type: "recipes-food-item", attributes: attrs } } },
          options,
        );
      },

      /** Update a food item. */
      async update(
        id: string,
        attrs: UpdateFoodItemAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesFoodItem>(
          patchAdminRecipesFoodItemsById,
          {
            path: { id },
            body: {
              data: { type: "recipes-food-item", id, attributes: attrs },
            },
          },
          options,
        );
      },

      /** Delete a food item. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesFoodItemsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── RecipeNutrition ───────────────────────────────────────────

    nutrition: {
      /** Get recipe nutrition by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesRecipeNutrition>(
          getAdminRecipesRecipeNutritionsById,
          { path: { id } },
          options,
        );
      },

      /** Get nutrition data for a recipe by recipe ID. */
      async getByRecipe(recipeId: string, options?: ListOptions) {
        return rb.execute<RecipesRecipeNutrition[]>(
          getAdminRecipesRecipeNutritionsRecipeByRecipeId,
          {
            path: { recipe_id: recipeId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create recipe nutrition. */
      async create(
        attrs: CreateRecipeNutritionAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeNutrition>(
          postAdminRecipesRecipeNutritions,
          {
            body: {
              data: { type: "recipes-recipe-nutrition", attributes: attrs },
            },
          },
          options,
        );
      },

      /** Update recipe nutrition. */
      async update(
        id: string,
        attrs: UpdateRecipeNutritionAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeNutrition>(
          patchAdminRecipesRecipeNutritionsById,
          {
            path: { id },
            body: {
              data: { type: "recipes-recipe-nutrition", id, attributes: attrs },
            },
          },
          options,
        );
      },
    },

    // ── Tag ───────────────────────────────────────────────────────

    tags: {
      /** Get a tag by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesTag>(
          getAdminRecipesTagsById,
          { path: { id } },
          options,
        );
      },

      /** List tags for an application. */
      async list(applicationId: string, options?: ListOptions) {
        return rb.execute<RecipesTag[]>(
          getAdminRecipesTagsApplicationByApplicationId,
          {
            path: { application_id: applicationId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a tag. */
      async create(attrs: CreateTagAttributes, options?: RequestOptions) {
        return rb.execute<RecipesTag>(
          postAdminRecipesTags,
          { body: { data: { type: "recipes-tag", attributes: attrs } } },
          options,
        );
      },

      /** Update a tag. */
      async update(
        id: string,
        attrs: UpdateTagAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesTag>(
          patchAdminRecipesTagsById,
          {
            path: { id },
            body: { data: { type: "recipes-tag", id, attributes: attrs } },
          },
          options,
        );
      },

      /** Delete a tag. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesTagsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── RecipeTag ──────────────────────────────────────────────────

    recipeTags: {
      /** Get a recipe-tag association by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesRecipeTag>(
          getAdminRecipesRecipeTagsById,
          { path: { id } },
          options,
        );
      },

      /** List recipe-tag associations for a recipe. */
      async listByRecipe(recipeId: string, options?: ListOptions) {
        return rb.execute<RecipesRecipeTag[]>(
          getAdminRecipesRecipeTagsRecipeByRecipeId,
          {
            path: { recipe_id: recipeId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a recipe-tag association (upsert). */
      async create(attrs: CreateRecipeTagAttributes, options?: RequestOptions) {
        return rb.execute<RecipesRecipeTag>(
          postAdminRecipesRecipeTags,
          { body: { data: { type: "recipes-recipe-tag", attributes: attrs } } },
          options,
        );
      },

      /** Delete a recipe-tag association. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesRecipeTagsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── RecipeFavorite ────────────────────────────────────────────

    favorites: {
      /** Get a favorite by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesRecipeFavorite>(
          getAdminRecipesFavoritesById,
          { path: { id } },
          options,
        );
      },

      /** List favorites for a workspace. */
      async list(workspaceId: string, options?: ListOptions) {
        return rb.execute<RecipesRecipeFavorite[]>(
          getAdminRecipesFavoritesWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** List favorites for a recipe. */
      async listByRecipe(recipeId: string, options?: ListOptions) {
        return rb.execute<RecipesRecipeFavorite[]>(
          getAdminRecipesFavoritesRecipeByRecipeId,
          {
            path: { recipe_id: recipeId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a recipe favorite. */
      async create(
        attrs: CreateRecipeFavoriteAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeFavorite>(
          postAdminRecipesFavorites,
          {
            body: {
              data: { type: "recipes-recipe-favorite", attributes: attrs },
            },
          },
          options,
        );
      },

      /** Update a recipe favorite. */
      async update(
        id: string,
        attrs: UpdateRecipeFavoriteAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeFavorite>(
          patchAdminRecipesFavoritesById,
          {
            path: { id },
            body: {
              data: { type: "recipes-recipe-favorite", id, attributes: attrs },
            },
          },
          options,
        );
      },

      /** Delete a recipe favorite. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesFavoritesById,
          { path: { id } },
          options,
        );
      },
    },

    // ── RecipeCollection ──────────────────────────────────────────

    collections: {
      /** Get a collection by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesRecipeCollection>(
          getAdminRecipesCollectionsById,
          { path: { id } },
          options,
        );
      },

      /** List collections for a workspace with optional filters. */
      async list(workspaceId: string, options?: CollectionListOptions) {
        return rb.execute<RecipesRecipeCollection[]>(
          getAdminRecipesCollectionsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
              ...(options?.contactId ? { contact_id: options.contactId } : {}),
              ...(options?.scope ? { scope: options.scope } : {}),
              ...(options?.sort ? { sort: options.sort } : {}),
            },
          },
          options,
        );
      },

      /** Create a recipe collection. */
      async create(
        attrs: CreateRecipeCollectionAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeCollection>(
          postAdminRecipesCollections,
          {
            body: {
              data: { type: "recipes-recipe-collection", attributes: attrs },
            },
          },
          options,
        );
      },

      /** Update a recipe collection. */
      async update(
        id: string,
        attrs: UpdateRecipeCollectionAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesRecipeCollection>(
          patchAdminRecipesCollectionsById,
          {
            path: { id },
            body: {
              data: {
                type: "recipes-recipe-collection",
                id,
                attributes: attrs,
              },
            },
          },
          options,
        );
      },

      /** Delete a recipe collection. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesCollectionsById,
          { path: { id } },
          options,
        );
      },

      // ── CollectionRecipe (nested) ─────────────────────────────

      recipes: {
        /** Get a collection recipe entry by ID. */
        async get(id: string, options?: RequestOptions) {
          return rb.execute<RecipesCollectionRecipe>(
            getAdminRecipesCollectionRecipesById,
            { path: { id } },
            options,
          );
        },

        /** List recipes in a collection. */
        async list(collectionId: string, options?: ListOptions) {
          return rb.execute<RecipesCollectionRecipe[]>(
            getAdminRecipesCollectionRecipesCollectionByCollectionId,
            {
              path: { collection_id: collectionId },
              query: {
                ...buildPageQuery(options?.page, options?.pageSize)?.query,
              },
            },
            options,
          );
        },

        /** Add a recipe to a collection. */
        async create(
          attrs: CreateCollectionRecipeAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesCollectionRecipe>(
            postAdminRecipesCollectionRecipes,
            {
              body: {
                data: { type: "recipes-collection-recipe", attributes: attrs },
              },
            },
            options,
          );
        },

        /** Update sort order of a collection recipe. */
        async update(
          id: string,
          attrs: UpdateCollectionRecipeAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesCollectionRecipe>(
            patchAdminRecipesCollectionRecipesById,
            {
              path: { id },
              body: {
                data: {
                  type: "recipes-collection-recipe",
                  id,
                  attributes: attrs,
                },
              },
            },
            options,
          );
        },

        /** Remove a recipe from a collection. */
        async delete(id: string, options?: RequestOptions) {
          return rb.execute<void>(
            deleteAdminRecipesCollectionRecipesById,
            { path: { id } },
            options,
          );
        },
      },
    },

    // ── EnrichmentJob ─────────────────────────────────────────────

    enrichmentJobs: {
      /** Get an enrichment job by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesEnrichmentJob>(
          getAdminRecipesEnrichmentJobsById,
          { path: { id } },
          options,
        );
      },

      /** List enrichment jobs for a recipe. */
      async list(recipeId: string, options?: ListOptions) {
        return rb.execute<RecipesEnrichmentJob[]>(
          getAdminRecipesEnrichmentJobsRecipeByRecipeId,
          {
            path: { recipe_id: recipeId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },
    },

    // ── MealSchedule ──────────────────────────────────────────────

    mealSchedules: {
      /** Get a meal schedule by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesMealSchedule>(
          getAdminRecipesMealSchedulesById,
          { path: { id } },
          options,
        );
      },

      /** List meal schedules for a workspace. */
      async list(workspaceId: string, options?: ListOptions) {
        return rb.execute<RecipesMealSchedule[]>(
          getAdminRecipesMealSchedulesWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a meal schedule. */
      async create(
        attrs: CreateMealScheduleAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesMealSchedule>(
          postAdminRecipesMealSchedules,
          {
            body: {
              data: { type: "recipes-meal-schedule", attributes: attrs },
            },
          },
          options,
        );
      },

      /** Update a meal schedule. */
      async update(
        id: string,
        attrs: UpdateMealScheduleAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesMealSchedule>(
          patchAdminRecipesMealSchedulesById,
          {
            path: { id },
            body: {
              data: { type: "recipes-meal-schedule", id, attributes: attrs },
            },
          },
          options,
        );
      },

      /** Delete a meal schedule. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesMealSchedulesById,
          { path: { id } },
          options,
        );
      },

      // ── ScheduledMeal (nested) ────────────────────────────────

      meals: {
        /** Get a scheduled meal by ID. */
        async get(id: string, options?: RequestOptions) {
          return rb.execute<RecipesScheduledMeal>(
            getAdminRecipesScheduledMealsById,
            { path: { id } },
            options,
          );
        },

        /** List scheduled meals for a meal schedule. */
        async list(mealScheduleId: string, options?: ListOptions) {
          return rb.execute<RecipesScheduledMeal[]>(
            getAdminRecipesScheduledMealsMealScheduleByMealScheduleId,
            {
              path: { meal_schedule_id: mealScheduleId },
              query: {
                ...buildPageQuery(options?.page, options?.pageSize)?.query,
              },
            },
            options,
          );
        },

        /** Create a scheduled meal. */
        async create(
          attrs: CreateScheduledMealAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesScheduledMeal>(
            postAdminRecipesScheduledMeals,
            {
              body: {
                data: { type: "recipes-scheduled-meal", attributes: attrs },
              },
            },
            options,
          );
        },

        /** Update a scheduled meal. */
        async update(
          id: string,
          attrs: UpdateScheduledMealAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesScheduledMeal>(
            patchAdminRecipesScheduledMealsById,
            {
              path: { id },
              body: {
                data: { type: "recipes-scheduled-meal", id, attributes: attrs },
              },
            },
            options,
          );
        },

        /** Delete a scheduled meal. */
        async delete(id: string, options?: RequestOptions) {
          return rb.execute<void>(
            deleteAdminRecipesScheduledMealsById,
            { path: { id } },
            options,
          );
        },
      },

      // ── NutritionSummary (nested) ─────────────────────────────

      nutritionSummaries: {
        /** Get a nutrition summary by ID. */
        async get(id: string, options?: RequestOptions) {
          return rb.execute<RecipesNutritionSummary>(
            getAdminRecipesNutritionSummariesById,
            { path: { id } },
            options,
          );
        },

        /** List nutrition summaries for a meal schedule. */
        async list(mealScheduleId: string, options?: ListOptions) {
          return rb.execute<RecipesNutritionSummary[]>(
            getAdminRecipesNutritionSummariesMealScheduleByMealScheduleId,
            {
              path: { meal_schedule_id: mealScheduleId },
              query: {
                ...buildPageQuery(options?.page, options?.pageSize)?.query,
              },
            },
            options,
          );
        },
      },
    },

    // ── ShoppingList ──────────────────────────────────────────────

    shoppingLists: {
      /** Get a shopping list by ID. */
      async get(id: string, options?: RequestOptions) {
        return rb.execute<RecipesShoppingList>(
          getAdminRecipesShoppingListsById,
          { path: { id } },
          options,
        );
      },

      /** List shopping lists for a workspace. */
      async list(workspaceId: string, options?: ListOptions) {
        return rb.execute<RecipesShoppingList[]>(
          getAdminRecipesShoppingListsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            query: {
              ...buildPageQuery(options?.page, options?.pageSize)?.query,
            },
          },
          options,
        );
      },

      /** Create a shopping list. */
      async create(
        attrs: CreateShoppingListAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesShoppingList>(
          postAdminRecipesShoppingLists,
          {
            body: {
              data: { type: "recipes-shopping-list", attributes: attrs },
            },
          },
          options,
        );
      },

      /** Update a shopping list. */
      async update(
        id: string,
        attrs: UpdateShoppingListAttributes,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesShoppingList>(
          patchAdminRecipesShoppingListsById,
          {
            path: { id },
            body: {
              data: { type: "recipes-shopping-list", id, attributes: attrs },
            },
          },
          options,
        );
      },

      /** Delete a shopping list. */
      async delete(id: string, options?: RequestOptions) {
        return rb.execute<void>(
          deleteAdminRecipesShoppingListsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Generate a shopping list from a meal schedule.
       * Aggregates all ingredients from scheduled meals.
       *
       * @param mealScheduleId - MealSchedule UUID
       * @param workspaceId - Workspace UUID
       * @param options - Request options
       * @returns Generated shopping list record
       */
      async generate(
        mealScheduleId: string,
        workspaceId: string,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesShoppingList>(
          postAdminRecipesShoppingListsGenerate,
          {
            body: {
              data: {
                meal_schedule_id: mealScheduleId,
                workspace_id: workspaceId,
              },
            },
          },
          options,
        );
      },

      /**
       * Regenerate an existing shopping list from its linked meal schedule.
       * Deletes existing items and recreates from current scheduled meals.
       *
       * @param shoppingListId - ShoppingList UUID
       * @param workspaceId - Workspace UUID
       * @param options - Request options
       * @returns Regenerated shopping list record
       */
      async regenerate(
        shoppingListId: string,
        workspaceId: string,
        options?: RequestOptions,
      ) {
        return rb.execute<RecipesShoppingList>(
          postAdminRecipesShoppingListsRegenerate,
          {
            body: {
              data: {
                shopping_list_id: shoppingListId,
                workspace_id: workspaceId,
              },
            },
          },
          options,
        );
      },

      // ── ShoppingListItem (nested) ─────────────────────────────

      items: {
        /** Get a shopping list item by ID. */
        async get(id: string, options?: RequestOptions) {
          return rb.execute<RecipesShoppingListItem>(
            getAdminRecipesShoppingListItemsById,
            { path: { id } },
            options,
          );
        },

        /** List items in a shopping list. */
        async list(shoppingListId: string, options?: ListOptions) {
          return rb.execute<RecipesShoppingListItem[]>(
            getAdminRecipesShoppingListItemsShoppingListByShoppingListId,
            {
              path: { shopping_list_id: shoppingListId },
              query: {
                ...buildPageQuery(options?.page, options?.pageSize)?.query,
              },
            },
            options,
          );
        },

        /** Create a shopping list item. */
        async create(
          attrs: CreateShoppingListItemAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesShoppingListItem>(
            postAdminRecipesShoppingListItems,
            {
              body: {
                data: { type: "recipes-shopping-list-item", attributes: attrs },
              },
            },
            options,
          );
        },

        /** Update a shopping list item. */
        async update(
          id: string,
          attrs: UpdateShoppingListItemAttributes,
          options?: RequestOptions,
        ) {
          return rb.execute<RecipesShoppingListItem>(
            patchAdminRecipesShoppingListItemsById,
            {
              path: { id },
              body: {
                data: {
                  type: "recipes-shopping-list-item",
                  id,
                  attributes: attrs,
                },
              },
            },
            options,
          );
        },

        /** Delete a shopping list item. */
        async delete(id: string, options?: RequestOptions) {
          return rb.execute<void>(
            deleteAdminRecipesShoppingListItemsById,
            { path: { id } },
            options,
          );
        },
      },
    },
  };
}
